/* eslint-disable fsecond/valid-event-listener */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-shadow */
import { AnimatePresence } from 'framer-motion'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getCookie } from 'react-use-cookie'
import { isEmpty } from 'remeda'
import useResizeObserver from 'use-resize-observer'
import { dataTestIds, promotionalBannerVariants } from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import { useHeader } from '@velasca/hooks/use-header'
import { useTop } from '@velasca/hooks/use-top'
import { AccessibilityIcon } from '@velasca/icons/accessibility'
import { AccountIcon } from '@velasca/icons/account'
import { ArrowDownIcon } from '@velasca/icons/arrow-down'
import { CartIcon } from '@velasca/icons/cart'
import { SearchIcon } from '@velasca/icons/search'
import type { Navbar } from '@velasca/types'
import { ConditionalLink } from '@velasca/ui/conditional-link'
import { IconButton } from '@velasca/ui/icon-button'
import { buildDprSources } from '@velasca/utils/build-dpr-sources'
import { $settings } from '@velasca/utils/globals'
import { handleError } from '@velasca/utils/handle-error'
import { setQueryParams } from '@velasca/utils/set-query-params'
import PromotionalBanner from '../PromotionalBanner'
import * as S from './styles'

const DesktopNavigation = ({
  menu,
  promotionalBanner,
  className = '',
  miscHeaderData = {},
  sticky = false,
  reduced = false,
}: Navbar) => {
  const {
    currentTemplate,
    searchEnabled,
    whatsappUrl,
    switchCountryPage,
    shippingReturnsPage,
    sizePage,
    phoneNumber,
    publisherMenuImages,
    currency,
  } = miscHeaderData

  const { t } = useTranslation('header')

  // An object containing current hover menu entry indexes
  const [activeIndexes, setActiveIndexes] = useState<{
    firstLevelIndex: number
    columnIndex?: number
    secondLevelIndex?: number
    thirdLevelIndex?: number
  } | null>(null)

  const isMenuOpen = useMemo(() => {
    return !!activeIndexes
  }, [activeIndexes])

  const top = useTop()
  const [hover, setHover] = useState(false)

  const newsletterCookie = getCookie('newsletter')

  const firstLevelIndex = useMemo(
    () => activeIndexes?.firstLevelIndex,
    [activeIndexes],
  )

  const currentMenu = useMemo(() => {
    if (menu && typeof firstLevelIndex !== 'undefined') {
      return menu[firstLevelIndex]
    }

    return null
  }, [menu, firstLevelIndex])

  const isInsideDirectLink = useMemo(() => {
    return Boolean(!currentMenu?.children && currentMenu?.url)
  }, [currentMenu])

  const publisherMenuImage = useMemo(() => {
    if (publisherMenuImages && typeof firstLevelIndex !== 'undefined') {
      return publisherMenuImages.find(
        (image) => Number(image.number) === firstLevelIndex + 1,
      )
    }

    return undefined
  }, [publisherMenuImages, firstLevelIndex])

  const currentMenuEntry = useMemo(() => {
    if (!activeIndexes || !currentMenu?.cols) {
      return null
    }

    const { columnIndex, secondLevelIndex, thirdLevelIndex } = activeIndexes

    if (
      typeof columnIndex === 'undefined' ||
      typeof secondLevelIndex === 'undefined'
    ) {
      return null
    }

    const secondLevel = currentMenu.cols[columnIndex][secondLevelIndex]

    if (secondLevel?.children && typeof thirdLevelIndex !== 'undefined') {
      return secondLevel.children[thirdLevelIndex]
    }

    if (typeof thirdLevelIndex === 'undefined') {
      return secondLevel
    }

    return null
  }, [activeIndexes, currentMenu])

  const imageTitle = useMemo(() => {
    if (currentMenuEntry) {
      if (currentMenuEntry.image) {
        return currentMenuEntry.title
      }

      return ''
    }

    // First level title
    if (publisherMenuImage?.src) {
      return publisherMenuImage.title || currentMenu?.title
    }

    return ''
  }, [currentMenuEntry, currentMenu, publisherMenuImage])

  const promotionalBannerRef = useRef<HTMLDivElement>(null)
  const helpMenuRef = useRef<HTMLDivElement>(null)
  const navbarRef = useRef<HTMLUListElement>(null)
  const smallLogoRef = useRef<SVGSVGElement>(null)

  const { ref: firstLevelStartChunkRef, width: firstLevelStartChunkRefWidth } =
    useResizeObserver<HTMLDivElement>({ box: 'border-box' })
  const { ref: firstLevelCenterChunkRef, width: firstLevelCenterChunkWidth } =
    useResizeObserver<HTMLUListElement>({ box: 'border-box' })
  const { ref: firstLevelEndChunkRef, width: firstLevelEndChunkWidth } =
    useResizeObserver<HTMLDivElement>({ box: 'border-box' })

  const secondLevelStartChunkRef = useRef<HTMLDivElement>(null)
  const secondLevelCenterChunkRef = useRef<HTMLDivElement>(null)
  const secondLevelEndChunkRef = useRef<HTMLDivElement>(null)

  const setPromotionalBannerHeight = useHeader(
    (s) => s.setPromotionalBannerHeight,
  )
  const setHelpMenuHeight = useHeader((s) => s.setHelpMenuHeight)
  const setNavbarHeight = useHeader((s) => s.setNavbarHeight)

  useEffect(() => {
    const { height = 0 } = helpMenuRef?.current?.getBoundingClientRect() || {}

    setHelpMenuHeight(height)
  }, [setHelpMenuHeight])

  useEffect(() => {
    const { height = 0 } =
      promotionalBannerRef?.current?.getBoundingClientRect() || {}

    setPromotionalBannerHeight(height)
  }, [setPromotionalBannerHeight])

  useEffect(() => {
    const { height = 0 } = navbarRef?.current?.getBoundingClientRect() || {}

    setNavbarHeight(height)
  }, [setNavbarHeight])

  const helpMenuHeight = useHeader((s) => s.helpMenuHeight)
  const headerHeight = useHeader((s) => s.headerHeight)

  // Serve per dare al menu aperto (secondo e terzo livello) le stesse dimensioni del menù di primo livello
  useEffect(() => {
    if (
      activeIndexes &&
      secondLevelStartChunkRef.current &&
      secondLevelCenterChunkRef.current &&
      secondLevelEndChunkRef.current
    ) {
      secondLevelStartChunkRef.current.style.width = `${firstLevelStartChunkRefWidth}px`
      secondLevelCenterChunkRef.current.style.width = `${firstLevelCenterChunkWidth}px`
      secondLevelEndChunkRef.current.style.width = `${firstLevelEndChunkWidth}px`
    }
  }, [
    secondLevelStartChunkRef,
    firstLevelStartChunkRefWidth,
    secondLevelCenterChunkRef,
    firstLevelCenterChunkWidth,
    secondLevelEndChunkRef,
    firstLevelEndChunkWidth,
    activeIndexes,
  ])

  // Serve per settare il padding top del body
  useEffect(() => {
    if (sticky) {
      document.body.style.paddingTop = `${headerHeight}px`
    }
  }, [sticky, headerHeight])

  // Serve per gestire il "finto scroll" delle parti dell'header
  useEffect(() => {
    if (reduced) {
      return
    }

    const listener = () => {
      if (helpMenuRef?.current && navbarRef?.current) {
        helpMenuRef.current.style.position = `absolute`

        helpMenuRef.current.style.top = `0`
        helpMenuRef.current.style.left = `0`
        helpMenuRef.current.style.right = `0`

        navbarRef.current.style.position = `absolute`
        navbarRef.current.style.top = `${helpMenuHeight}px`
        navbarRef.current.style.left = `0`
        navbarRef.current.style.right = `0`

        if (window.scrollY >= 0) {
          helpMenuRef.current.style.top = `${-window.scrollY}px`

          if (window.scrollY > helpMenuHeight) {
            navbarRef.current.style.top = `0`
            if (smallLogoRef.current) {
              smallLogoRef.current.style.opacity = `1`
            }
          } else {
            navbarRef.current.style.top = `${helpMenuHeight - window.scrollY}px`
            if (smallLogoRef.current) {
              smallLogoRef.current.style.opacity = `0`
            }
          }
        }
      }
    }

    // La eseguo anche al mount perché non so qual è la posizione dello scroll
    // (potrebbe esserci un mount con scroll a metà pagina)
    listener()

    window.addEventListener('scroll', listener)

    return () => {
      window.removeEventListener('scroll', listener)
    }
  }, [reduced, helpMenuHeight])

  const { cartItemCount, setIsDrawerOpen } = useCart()

  // eslint-disable-next-line
  const handleAccessibilityToggle = () => {
    try {
      // @ts-expect-error
      toggleAccessEzWidgetManually() // eslint-disable-line
    } catch (error) {
      handleError(error, { message: 'AccessEz widget not working.' })
    }
  }

  return (
    <S.Header
      className={className}
      isOpen={isMenuOpen}
      hover={hover}
      top={top}
      sticky={Boolean(sticky)}
      onMouseEnter={() => {
        if (sticky) {
          return
        }
        setHover(true)
      }}
      onMouseLeave={() => {
        if (!isMenuOpen || isInsideDirectLink) {
          setHover(false)
        }
        setActiveIndexes(null)
      }}
    >
      <PromotionalBanner
        ref={promotionalBannerRef}
        {...promotionalBanner}
        variant={
          top && !isMenuOpen && !hover && !sticky
            ? promotionalBannerVariants.SEMI_TRANSPARENT
            : promotionalBannerVariants.PRIMARY
        }
      />
      <S.Background>
        {!reduced && (
          <S.HelpMenu
            ref={helpMenuRef}
            isOpen={isMenuOpen}
            hover={hover}
            top={top}
            sticky={Boolean(sticky)}
          >
            <S.HelpMenuContent>
              <S.StartHelpChunk>
                {shippingReturnsPage && t('returns') && (
                  <S.HelpLink href={shippingReturnsPage}>
                    {t('returns')}
                  </S.HelpLink>
                )}

                {sizePage && t('size') && (
                  <>
                    {shippingReturnsPage && <S.Separator />}
                    <S.HelpLink href={sizePage}>{t('size')}</S.HelpLink>
                  </>
                )}

                {!newsletterCookie && t('newsletter') && (
                  <>
                    {(shippingReturnsPage || sizePage) && <S.Separator />}
                    <S.HelpLink
                      onClick={() => {
                        window.showNLBanner(true)
                      }}
                    >
                      {t('newsletter')}
                    </S.HelpLink>
                  </>
                )}
              </S.StartHelpChunk>
              <S.CenterHelpChunk>
                {currentTemplate === 'index' ? (
                  <h1>
                    <S.StyledBrandLogoExtended
                      variant={
                        isMenuOpen || hover || !top || sticky
                          ? 'primary'
                          : 'white'
                      }
                    />
                  </h1>
                ) : (
                  <S.Link href="/" title="Velasca">
                    <S.StyledBrandLogoExtended
                      variant={
                        isMenuOpen || hover || !top || sticky
                          ? 'primary'
                          : 'white'
                      }
                    />
                  </S.Link>
                )}
              </S.CenterHelpChunk>
              <S.EndHelpChunk>
                {currency?.supported && (
                  <>
                    <S.CurrencySelector>
                      <>
                        <S.Select currencies={currency?.supported} />
                        <S.IconWrapper>
                          <IconButton
                            iconSize="1rem"
                            clickAreaSize="2rem"
                            variant={
                              isMenuOpen || hover || !top || sticky
                                ? 'primary'
                                : 'secondary'
                            }
                          >
                            <ArrowDownIcon />
                          </IconButton>
                        </S.IconWrapper>
                      </>
                    </S.CurrencySelector>
                    <S.Separator />
                  </>
                )}
                <S.HelpLink href={switchCountryPage}>
                  {t('changeCountry')}
                </S.HelpLink>
                <S.Separator />
                {phoneNumber ? (
                  <S.HelpLink href={`tel:${phoneNumber}`}>{`${t(
                    'phone',
                  )} ${phoneNumber}`}</S.HelpLink>
                ) : null}
                <S.Separator />
                <S.HelpLink href={whatsappUrl}>Whatsapp</S.HelpLink>
              </S.EndHelpChunk>
            </S.HelpMenuContent>
          </S.HelpMenu>
        )}
        <S.Navbar
          isOpen={isMenuOpen}
          hover={hover}
          top={top}
          sticky={Boolean(sticky)}
          ref={navbarRef}
        >
          <S.MenuBackground>
            <S.NavHead>
              <S.StartChunk ref={firstLevelStartChunkRef}>
                <S.LogoLink href="/">
                  <S.StyledBrandLogoShort
                    ref={smallLogoRef}
                    $alwaysShow={reduced}
                  />
                </S.LogoLink>
              </S.StartChunk>
              <S.CenterChunk ref={firstLevelCenterChunkRef}>
                {menu?.map((firstLevel, firstLevelIndex) => {
                  return (
                    <li
                      key={
                        firstLevel.id ??
                        `${firstLevel.handle}-${firstLevelIndex}`
                      }
                    >
                      <ConditionalLink
                        href={firstLevel.url}
                        condition={Boolean(firstLevel?.url)}
                        CustomLink={S.NavLink}
                        target={firstLevel.isTargetBlank ? '_blank' : '_self'}
                      >
                        <S.FirstLevelMenuTitle
                          title={firstLevel.title}
                          isSelected={
                            firstLevelIndex === activeIndexes?.firstLevelIndex
                          }
                          onMouseEnter={() => {
                            setActiveIndexes({ firstLevelIndex })
                          }}
                        >
                          {firstLevel.title}
                        </S.FirstLevelMenuTitle>
                      </ConditionalLink>
                    </li>
                  )
                })}
              </S.CenterChunk>
              <S.EndChunk ref={firstLevelEndChunkRef}>
                {$settings.accessibly_enabled ? (
                  <S.AccessibilityButton
                    className="accessibility-icon"
                    variant={
                      isMenuOpen || hover || !top || sticky
                        ? 'primary'
                        : 'secondary'
                    }
                    onClick={handleAccessibilityToggle}
                  >
                    <AccessibilityIcon />
                  </S.AccessibilityButton>
                ) : null}
                {searchEnabled && (
                  <IconButton
                    variant={
                      isMenuOpen || hover || !top || sticky
                        ? 'primary'
                        : 'secondary'
                    }
                  >
                    <SearchIcon />
                  </IconButton>
                )}
                <IconButton
                  badge={cartItemCount}
                  variant={
                    isMenuOpen || hover || !top || sticky
                      ? 'primary'
                      : 'secondary'
                  }
                  onClick={() => {
                    setIsDrawerOpen(true)
                  }}
                >
                  <CartIcon />
                </IconButton>
                <IconButton
                  link="/account"
                  variant={
                    isMenuOpen || hover || !top || sticky
                      ? 'primary'
                      : 'secondary'
                  }
                >
                  <AccountIcon />
                </IconButton>
              </S.EndChunk>
            </S.NavHead>
            <AnimatePresence
              onExitComplete={() => {
                !isInsideDirectLink && setHover(false)
              }}
            >
              {currentMenu?.children && (
                <S.MenuLinksBackground
                  isOpen={isMenuOpen}
                  hover={hover}
                  top={top}
                  sticky={Boolean(sticky)}
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
                  data-testid={dataTestIds.MENU_LINKS}
                >
                  <S.MenuLinks>
                    <div ref={secondLevelStartChunkRef} />
                    <S.CenterMenuLinksContainer ref={secondLevelCenterChunkRef}>
                      {currentMenu?.cols?.map((col, i) => {
                        return (
                          <S.MenuColumn key={`menu-links-col${i}`}>
                            {col.map((secondLevel, j) => {
                              return (
                                <S.SecondLevelGroup
                                  key={
                                    secondLevel.id ??
                                    `${secondLevel.handle}-${j}`
                                  }
                                >
                                  <S.SecondLevelMenuTitle
                                    onMouseEnter={() => {
                                      if (activeIndexes) {
                                        setActiveIndexes({
                                          firstLevelIndex:
                                            activeIndexes.firstLevelIndex,
                                          columnIndex: i,
                                          secondLevelIndex: j,
                                        })
                                      }
                                    }}
                                  >
                                    <ConditionalLink
                                      href={secondLevel.url}
                                      CustomLink={S.NavLink}
                                      target={
                                        secondLevel.isTargetBlank
                                          ? '_blank'
                                          : '_self'
                                      }
                                    >
                                      {secondLevel.title}
                                    </ConditionalLink>
                                  </S.SecondLevelMenuTitle>
                                  {secondLevel.children &&
                                    !isEmpty(secondLevel.children) && (
                                      <S.SecondLevelMenuChildren>
                                        {secondLevel.children?.map(
                                          (thirdLevel, k) => {
                                            return (
                                              <S.ThirdLevelMenuTitle
                                                key={
                                                  thirdLevel.id ??
                                                  `${thirdLevel.handle}-${k}`
                                                }
                                                onMouseEnter={() => {
                                                  if (activeIndexes) {
                                                    setActiveIndexes({
                                                      firstLevelIndex:
                                                        activeIndexes.firstLevelIndex,
                                                      columnIndex: i,
                                                      secondLevelIndex: j,
                                                      thirdLevelIndex: k,
                                                    })
                                                  }
                                                }}
                                              >
                                                <ConditionalLink
                                                  href={thirdLevel.url}
                                                  CustomLink={S.NavLink}
                                                  target={
                                                    thirdLevel.isTargetBlank
                                                      ? '_blank'
                                                      : '_self'
                                                  }
                                                >
                                                  {thirdLevel.title}
                                                </ConditionalLink>
                                              </S.ThirdLevelMenuTitle>
                                            )
                                          },
                                        )}
                                      </S.SecondLevelMenuChildren>
                                    )}
                                </S.SecondLevelGroup>
                              )
                            })}
                          </S.MenuColumn>
                        )
                      })}
                      <S.MediaColumn>
                        {typeof firstLevelIndex !== 'undefined' && (
                          <ConditionalLink
                            CustomLink={S.ImageLink}
                            href={
                              (!currentMenuEntry && publisherMenuImage?.url) ||
                              currentMenuEntry?.url ||
                              ''
                            }
                          >
                            <>
                              <S.ImagesWrapper>
                                {/* Prints all publisher images */}
                                {publisherMenuImages?.map(
                                  (publisherMenuImage) => (
                                    <Image
                                      key={`menu-image-${publisherMenuImage.number}`}
                                      src={publisherMenuImage.src}
                                      visible={
                                        Number(publisherMenuImage.number) ===
                                          firstLevelIndex + 1 &&
                                        typeof activeIndexes?.secondLevelIndex ===
                                          'undefined' &&
                                        typeof activeIndexes?.thirdLevelIndex ===
                                          'undefined'
                                      }
                                      alt={
                                        publisherMenuImage?.alt ||
                                        currentMenuEntry?.title ||
                                        ''
                                      }
                                    />
                                  ),
                                )}
                                {/* Prints all second and third level images */}
                                {currentMenu.cols?.map((col, columnIndex) =>
                                  col.map(
                                    (secondLevelEntry, secondLevelIndex) => (
                                      <Fragment
                                        key={`menu-image-col-${secondLevelIndex}`}
                                      >
                                        {secondLevelEntry?.image ? (
                                          <Image
                                            src={secondLevelEntry.image}
                                            alt={secondLevelEntry.title}
                                            visible={
                                              activeIndexes?.columnIndex ===
                                                columnIndex &&
                                              activeIndexes?.secondLevelIndex ===
                                                secondLevelIndex &&
                                              typeof activeIndexes?.thirdLevelIndex ===
                                                'undefined'
                                            }
                                            onError={(e) => {
                                              e.currentTarget.style.display =
                                                'none'
                                            }}
                                          />
                                        ) : (
                                          <S.EmptyImage
                                            key={`empty-image-col${columnIndex}-third-level-item-${secondLevelIndex}`}
                                            visible={
                                              activeIndexes?.columnIndex ===
                                                columnIndex &&
                                              activeIndexes?.secondLevelIndex ===
                                                secondLevelIndex &&
                                              typeof activeIndexes?.thirdLevelIndex ===
                                                'undefined'
                                            }
                                          />
                                        )}
                                        {secondLevelEntry?.children?.map(
                                          (thirdLevelEntry, thirdLevelIndex) =>
                                            thirdLevelEntry?.image ? (
                                              <Image
                                                key={`image-col${columnIndex}-third-level-item-${secondLevelIndex}-child-${thirdLevelIndex}`}
                                                src={thirdLevelEntry.image}
                                                alt={thirdLevelEntry.title}
                                                visible={
                                                  activeIndexes?.columnIndex ===
                                                    columnIndex &&
                                                  activeIndexes?.secondLevelIndex ===
                                                    secondLevelIndex &&
                                                  activeIndexes?.thirdLevelIndex ===
                                                    thirdLevelIndex
                                                }
                                                onError={(e) => {
                                                  e.currentTarget.style.display =
                                                    'none'
                                                }}
                                              />
                                            ) : (
                                              <S.EmptyImage
                                                key={`empty-image-col${columnIndex}-third-level-item-${secondLevelIndex}-child-${thirdLevelIndex}`}
                                                visible={
                                                  activeIndexes?.columnIndex ===
                                                    columnIndex &&
                                                  activeIndexes?.secondLevelIndex ===
                                                    secondLevelIndex &&
                                                  activeIndexes?.thirdLevelIndex ===
                                                    thirdLevelIndex
                                                }
                                              />
                                            ),
                                        )}
                                      </Fragment>
                                    ),
                                  ),
                                )}
                              </S.ImagesWrapper>
                              <S.ImageTitle>{imageTitle}</S.ImageTitle>
                            </>
                          </ConditionalLink>
                        )}
                      </S.MediaColumn>
                    </S.CenterMenuLinksContainer>
                    <div ref={secondLevelEndChunkRef} />
                  </S.MenuLinks>
                </S.MenuLinksBackground>
              )}
            </AnimatePresence>
          </S.MenuBackground>
        </S.Navbar>
      </S.Background>
    </S.Header>
  )
}

interface ImageProps {
  src: string
  alt: string
  visible: boolean
  lazy?: boolean
  onError?: (e: any) => void
}

const Image = ({ src, alt, visible, lazy = false, onError }: ImageProps) => {
  if (!src) {
    return <S.EmptyImage visible={visible} />
  }

  return (
    <S.Image
      visible={visible}
      lqip={lazy}
      lazyload={lazy}
      imgSrc={setQueryParams(src, [{ name: 'width', value: '30' }])}
      alt={alt}
      sources={buildDprSources(src, [
        { width: 200, dpr: 1 },
        { width: 400, dpr: 2 },
        { width: 600, dpr: 3 },
      ])}
      onError={onError}
    />
  )
}

export default DesktopNavigation
